import { Box, Button, SystemIcon, Typography } from '@wishket/design-system';
import Link from 'next/link';
import React from 'react';

import { FadeUpContainer, Icon, IconName } from '@/shared/Components';
import { PATH } from '@/shared/constants';

interface AskAIRecommendationCardProps {
  isLoggedIn: boolean;
}

type Property = {
  iconName: IconName;
  title: {
    desktop: string;
    mobile: string;
  };
  description: {
    desktop: string;
    mobile: string;
  };
  href: string;
  buttonText: string;
};

const Properties: Record<'member' | 'guest', Property> = {
  member: {
    iconName: 'heart_face',
    title: {
      desktop: '내 실무에 도움 될 작가를 찾고 있다면?',
      mobile: '실무에 도움 될 작가를 찾고 있다면?',
    },
    description: {
      desktop: 'IT 전문가로 이루어진 요즘 작가들의 지식과 경험을 만나보세요.',
      mobile: '요즘 작가들 보러가기',
    },
    href: PATH.AUTHORS,
    buttonText: '보러가기',
  },
  guest: {
    iconName: 'rocket',
    title: {
      desktop: '최신 IT 트렌드를 빠르게 받고 싶다면?',
      mobile: 'IT 트렌드를 빠르게 받고 싶다면?',
    },
    description: {
      desktop: '회원가입하고 무료로 뉴스레터를 받아보세요.',
      mobile: '회원가입하고 뉴스레터 받기',
    },
    href: `${process.env.NEXT_PUBLIC_APP_AUTH_URL}/signup?next=${PATH.QUESTIONS}`,
    buttonText: '회원가입',
  },
};

const AskAIRecommendationCard = ({
  isLoggedIn,
}: AskAIRecommendationCardProps) => {
  const property = isLoggedIn ? Properties.member : Properties.guest;
  const { iconName, title, description, href, buttonText } = property;

  return (
    <FadeUpContainer>
      <Box
        className="flex items-center gap-6 self-stretch rounded-3xl bg-w-gray-10 p-4 typo-body2 desktop:px-8 desktop:py-6 desktop:typo-body1"
        data-testid="askAI-recommendation-card-root"
      >
        <Icon name={iconName} />
        <Box className="flex flex-1 flex-col gap-1">
          <Typography
            variant="body2"
            className="text-w-gray-900 desktop:hidden"
          >
            {title.mobile}
          </Typography>
          <Link
            href={href}
            className="flex items-center gap-0.5 desktop:hidden"
          >
            <Typography variant="body2" className="font-medium text-primary">
              {description.mobile}
            </Typography>
            <SystemIcon name="small_arrow_right" className="text-primary" />
          </Link>
          <Typography
            variant="body1"
            className="hidden text-w-gray-900 desktop:block"
          >
            {title.desktop}
          </Typography>
          <Typography
            variant="body1"
            className="hidden text-w-gray-600 desktop:block"
          >
            {description.desktop}
          </Typography>
        </Box>
        <Link
          className="hidden desktop:block"
          href={href}
          target="_blank"
          data-testid="askAI-recommendation-card-link"
        >
          <Button variant="solid" size="sm">
            {buttonText}
          </Button>
        </Link>
      </Box>
    </FadeUpContainer>
  );
};

export default AskAIRecommendationCard;
